import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { CVT_EVENTS } from 'Utilities/analytics';

import VehicleImage from 'Components/misc/VehicleImage/VehicleImage';
import helpers from 'Containers/app/account/Components/Input/helpers';
import { CUSTOMER_HUB_MILEAGE_ROUTE, CUSTOMER_HUB_VALUATION_ROUTE } from 'Containers/app/customerHub/CustomerHubHelpers';
import LocalTexts from 'Containers/app/customerHub/CustomerHubTexts.json';
import HomeVehicle from 'Containers/app/fastlane/homeVRM/HomeVehicle';
import TrustPilotImage from 'Containers/app/fastlane/TrustPilotImage/TrustPilotImage';
import VrmInputMileage from 'Containers/app/fastlane/vrmInput/VrmInputMileage';
import type { VrmInputVRMProps } from 'Containers/app/fastlane/vrmInput/VrmInputVRM';
import VrmInputVRM from 'Containers/app/fastlane/vrmInput/VrmInputVRM';
import { Content } from 'LegacySBCRA/Content/Content';

import styles from './TrackerHero.scss';

const LocalT = new MDText(LocalTexts);

const getStraplineText = () => ({
  eyebrow: LocalT.translate('valuation.label'),
  strap: LocalT.translate('valuation.strap'),
  titleLine1: LocalT.translate('valuation.title.0'),
});

const TrackerHero = () => {
  const [heroHeight, setHeroHeight] = useState<undefined | string>();
  const ref = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { pathname } = useLocation();
  const { vehicleLookupActions, vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const text = getStraplineText();

  const onVrmSuccess = () => {
    const height = ref?.current?.clientHeight;
    if (height) {
      setHeroHeight(`${height}px`);
    }
    history.push(CUSTOMER_HUB_MILEAGE_ROUTE);
  };

  const onVrmError = (vrm: string, errorCode: string, setWrongVrm?: (arg: boolean) => {}) => {
    setWrongVrm?.(false); // Re-intialize state.
    const { detail, title } = helpers.getVrmErrorTexts(errorCode, vrm);
    return toast({
      content: detail,
      heading: title,
      variant: 'error',
    });
  };

  const setMileageUrl = () => {
    history.push(CUSTOMER_HUB_VALUATION_ROUTE);
  };

  const onSubmitMileage = (mileage: number) => {
    vehicleLookupActions.update({ trackerMileage: mileage });
    history.push(`${CUSTOMER_HUB_VALUATION_ROUTE}/${vehicle.vrm || ''}`);
  };

  const isMileageStep = pathname === CUSTOMER_HUB_MILEAGE_ROUTE;

  useEffect(() => {
    if (isMileageStep && !vehicle?.id) {
      history.push(CUSTOMER_HUB_VALUATION_ROUTE);
    }
  }, [history, isMileageStep, vehicle?.id]);

  const mileageContent = (isMileageStep) ? (
    <>
      {vehicle && <HomeVehicle valuationHub vehicle={vehicle} />}
      <VrmInputMileage
        valuationHub
        analyticsEvents={ {
          onButtonClick: (vrm: string) => CVT_EVENTS.MILEAGE_CTA_CLICKED({ vrm }),
          onInputFocus: () => CVT_EVENTS.MILEAGE_INPUT_FOCUSED(),
        }}
        onSubmit={onSubmitMileage}
        setShowMileage={() => setMileageUrl()}
      />
    </>
  ) : (
    <>
      <span className={styles.eyebrow}>{text.eyebrow}</span>
      <h1 className={styles.heading}>
        {text.titleLine1}
      </h1>
      <p className={styles.strap}>
        {text.strap}
      </p>
      <VrmInputVRM
        valuationHub
        analyticsEvents={ {
          onButtonClick: () => CVT_EVENTS.VRM_CTA_CLICKED(),
          onInputFocus: () => CVT_EVENTS.VRM_INPUT_FOCUSED(),
        }}
        onError={onVrmError as VrmInputVRMProps['onError']}
        onSuccess={onVrmSuccess}
        vrm={vehicle?.vrm}
      />
      <TrustPilotImage className={styles.trustpilot} />
    </>
  );

  return (
    <div className={styles.component}>
      <Content className={styles.heroOuter} wrapper={{ className: styles.wrapper }}>
        <div className={styles.heroContent}>
          <main ref={ref} className={styles.hero} style={{ '--hero-height': heroHeight } as React.CSSProperties}>
            <div className={cx(styles.vrm, { [styles.mileage]: isMileageStep })}>
              {mileageContent}
            </div>
          </main>
        </div>
        <VehicleImage
          className={styles.vehicleImage}
          name="cvt_hero"
        />
      </Content>
    </div>
  );
};

export default TrackerHero;
